import { setIsProxyHealthDialogOpen } from "../../../state";
import { getProxyHeaders, getProxyUrl } from "../../api/proxyHeaders";

export type HealthData = {
    status: string;
    version: string;
    date: string;
};

const HEALTH_CHECK_TIMEOUT_MS = 5000;

// A bare fetch() can hang indefinitely on a stalled TV/proxy connection — since
// _get() awaits checkProxyStatus() before it ever sets up its own timeout, an
// untimed health check here silently defeats every caller's timeout budget.
async function fetchHealthEndpoint(timeoutMs: number = HEALTH_CHECK_TIMEOUT_MS) {
    // AbortController is missing on Chromium < 66 (Tizen 5.0 and older); guard it
    // so the health check itself doesn't throw a ReferenceError there.
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = setTimeout(() => controller?.abort(), timeoutMs);
    try {
        return await fetch(`${getProxyUrl()}health`, {
            headers: getProxyHeaders(),
            ...(controller ? { signal: controller.signal } : {}),
        });
    } finally {
        clearTimeout(timeoutId);
    }
}

// Full health payload (status + version + date) for display on the User Info screen.
// The date/version aren't always present in the same shape, so they're parsed on a
// best-effort basis and fall back to "Unknown" — only the leading "OK" determines status.
export const checkProxyHealth = async (): Promise<HealthData | null> => {
    try {
        const response = await fetchHealthEndpoint();
        const text = await response.text();
        const trimmed = text.trim();

        if (!/^OK\b/.test(trimmed)) {
            console.warn(`Proxy Health Check - not OK, raw response: ${JSON.stringify(text)}`);
            return null;
        }

        const versionMatch = trimmed.match(/(\d+\.\d+\.\d+)/);
        const dateMatch = trimmed.match(/(\d{2}\/\d{2}\/\d{4})/);

        return {
            status: 'OK',
            version: versionMatch?.[1] ?? 'Unknown',
            date: dateMatch?.[1] ?? 'Unknown',
        };
    } catch (err) {
        console.error(err);
        return null;
    }
}

// checkProxyStatus() gates every single api._get() call in VPN mode, and the home
// screen alone fires it concurrently for channels/movies/series (plus one more per
// infinite-scroll page after that). Without caching, that's several independent
// /health round-trips for what is effectively the same answer a few seconds apart —
// each one an extra chance for a transient stall to fail a request that would
// otherwise have succeeded. Cache the result briefly and share one in-flight
// request across concurrent callers instead of firing one each.
const STATUS_CACHE_MS = 8000;
let cachedStatus: { value: boolean; timestamp: number } | null = null;
let inFlightStatus: Promise<boolean> | null = null;

export const checkProxyStatus = async (): Promise<boolean> => {
    const now = Date.now();
    if (cachedStatus && now - cachedStatus.timestamp < STATUS_CACHE_MS) {
        return cachedStatus.value;
    }
    if (inFlightStatus) {
        return inFlightStatus;
    }

    inFlightStatus = (async () => {
        try {
            const response = await fetchHealthEndpoint();
            const text = await response.text();

            // Only the leading "OK" matters — version/commit/date are informational
            // and can change shape without meaning the proxy is unhealthy.
            const isHealthy = /^OK\b/.test(text.trim());
            console.log(`Proxy Health Status: ${isHealthy ? 'OK' : 'NOT OK'} (raw: ${JSON.stringify(text)})`);
            cachedStatus = { value: isHealthy, timestamp: Date.now() };
            return isHealthy;
        } catch (err) {
            console.error(err);
            cachedStatus = { value: false, timestamp: Date.now() };
            return false;
        } finally {
            inFlightStatus = null;
        }
    })();

    return inFlightStatus;
}

export const isProxyHealthOk = async (t: any): Promise<boolean> => {
    const proxyHealthy = await checkProxyStatus();

    if (!proxyHealthy) {
        showProxyHealthMessage();
        return false;
    }
    return true;
};

// Guards against overlapping timers: several concurrent failed health checks
// (e.g. channels/movies/series all failing at once) would otherwise each start
// their own 5s close timer, and an earlier one firing could close the dialog
// out from under a later, still-relevant failure.
let hideDialogTimeoutId: ReturnType<typeof setTimeout> | null = null;

export const showProxyHealthMessage = () => {
    setIsProxyHealthDialogOpen(true);
    if (hideDialogTimeoutId) clearTimeout(hideDialogTimeoutId);
    hideDialogTimeoutId = setTimeout(() => {
        setIsProxyHealthDialogOpen(false);
        hideDialogTimeoutId = null;
    }, 5000);
}